export const formatCurrency = (valor = 0, moneda = "USD", locale = "es-SV") =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: moneda,
  }).format(valor);

export function formatNumber(value, minDecimals = 2, maxDecimals = 8) {
  // Intentar convertir si es un string con un número válido
  if (typeof value === "string") {
    const parsedValue = parseFloat(value.replace(/,/g, ""));
    if (!isNaN(parsedValue)) {
      value = parsedValue;
    } else {
      return "0"; // Si no es numérico, retorna "0"
    }
  }

  // Si no es un número válido después de la conversión
  if (typeof value !== "number" || isNaN(value)) return "0";

  // Asegurar que minDecimals no sea mayor que maxDecimals
  if (minDecimals > maxDecimals) {
    [minDecimals, maxDecimals] = [maxDecimals, minDecimals];
  }

  // Formatear el número con el máximo de decimales permitidos
  let decimalPart = value.toFixed(maxDecimals).replace(/(\.0+|0+)$/, ""); // Eliminar ceros innecesarios

  // Si `minDecimals` es 0 y no hay decimales significativos, no mostrar el punto
  if (minDecimals === 0 && !decimalPart.includes(".")) {
    return value.toLocaleString(); // Retorna solo la parte entera con separadores de miles
  }

  // Si `minDecimals` es mayor a 0, aseguramos que tenga al menos esa cantidad de decimales
  if (minDecimals > 0) {
    const [integerPart, decimals = ""] = decimalPart.split(".");

    // Si los decimales son menos que `minDecimals`, los completamos con ceros
    const neededZeros = minDecimals - decimals.length;
    const finalDecimals = decimals + "0".repeat(Math.max(0, neededZeros));

    // Formatear la parte entera con separadores de miles
    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    return `${formattedIntegerPart}.${finalDecimals}`;
  }

  // Si no se requiere decimales mínimos, devolver el número sin el punto
  return decimalPart;
}

export function redondearDecimales(numero, decimales = 8) {
  // Convertir a número de manera segura
  let num = Number(numero);

  // Validar si el número es NaN (ejemplo: si pasaron un string inválido)
  if (isNaN(num)) return 0;

  let factor = Math.pow(10, decimales + 1); // Factor para redondeo
  let numStr = (Math.round(num * factor) / factor).toFixed(decimales + 1); // Asegurar un decimal extra

  let partes = numStr.split(".");
  if (partes.length < 2) return Number(num.toFixed(decimales)); // Si no tiene decimales, devolver como número

  let parteDecimal = partes[1];

  if (parteDecimal.length <= decimales) return Number(num.toFixed(decimales)); // Si tiene menos decimales, devolver directo

  let ultimoDecimal = parseInt(parteDecimal[decimales]); // Obtener el decimal siguiente al límite
  let resultado = Number(num.toFixed(decimales)); // Inicializar el resultado

  // Aplicar redondeo si el decimal siguiente es 5 o mayor
  if (ultimoDecimal >= 5) {
    resultado += Math.pow(10, -decimales);
  }

  return Number(resultado.toFixed(decimales)); // Asegurar que se retorne como número
}

export function numeroALetras(num, moneda = "dolares") {
  if (num === 0) return `cero ${moneda}`;

  const unidades = ["", "uno", "dos", "tres", "cuatro", "cinco", "seis", "siete", "ocho", "nueve"];
  const especiales = [
    "diez",
    "once",
    "doce",
    "trece",
    "catorce",
    "quince",
    "dieciséis",
    "diecisiete",
    "dieciocho",
    "diecinueve",
  ];
  const decenas = ["", "", "veinte", "treinta", "cuarenta", "cincuenta", "sesenta", "setenta", "ochenta", "noventa"];
  const centenas = [
    "",
    "cien",
    "doscientos",
    "trescientos",
    "cuatrocientos",
    "quinientos",
    "seiscientos",
    "setecientos",
    "ochocientos",
    "novecientos",
  ];

  function convertirMenosDeMil(n) {
    if (n === 100) return "cien";
    let palabras = "";

    if (n >= 100) {
      let cent = Math.floor(n / 100);
      palabras += centenas[cent] + " ";
      n %= 100;
    }

    if (n >= 20) {
      let dec = Math.floor(n / 10);
      palabras += decenas[dec];
      n %= 10;
      if (n > 0) palabras += " y ";
    } else if (n >= 10) {
      palabras += especiales[n - 10];
      n = 0;
    }

    if (n > 0) palabras += unidades[n];

    return palabras.trim();
  }

  function convertirMiles(n) {
    if (n < 1000) return convertirMenosDeMil(n);
    let palabras = "";

    if (n >= 1000000) {
      let millones = Math.floor(n / 1000000);
      palabras += (millones === 1 ? "un millón" : convertirMenosDeMil(millones) + " millones") + " ";
      n %= 1000000;
    }

    if (n >= 1000) {
      let miles = Math.floor(n / 1000);
      palabras += (miles === 1 ? "mil" : convertirMenosDeMil(miles) + " mil") + " ";
      n %= 1000;
    }

    if (n > 0) palabras += convertirMenosDeMil(n);

    return palabras.trim();
  }

  function convertirNumero(num) {
    let partes = num.toString().split(".");
    let entero = parseInt(partes[0], 10);
    let resultado = convertirMiles(entero) + ` ${moneda}`;

    if (partes.length > 1) {
      let decimales = partes[1].padEnd(2, "0").slice(0, 2);
      resultado += ` con ${convertirMenosDeMil(parseInt(decimales, 10))} centavos`;
    }

    return resultado.toUpperCase();
  }

  return convertirNumero(num);
}
